'use client';

import { useMemo, useState } from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import fuzzysort from 'fuzzysort';
import { FilterIcon } from 'lucide-react';

import { CreateServiceTypeTemplateDialog } from '@/app/(app)/settings/service_types/dialog';
import { Popover } from '@/components/atoms/popover';
import { Select } from '@/components/atoms/select';
import { DataTable } from '@/components/data-table';
import { useServiceTypeTemplatesCtx } from '@/components/providers/service-templates-provider';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { SERVICE_TYPE_TEMPLATE_COLUMNS } from './columns';

interface Filters {
  search: string;
  status: ApiSchemas['ActiveInactive'] | null;
}

export function ServiceTypeTemplatesTable() {
  const { serviceTypeTemplates } = useServiceTypeTemplatesCtx();
  const [filters, setFilters] = useState<Filters>({
    search: '',
    status: null,
  });

  const serviceTypeTemplatesWithSearch = useMemo(() => {
    return serviceTypeTemplates.map((template) => ({
      ...template,
      _search: [template.name, template.description].filter(Boolean).join(' '),
    }));
  }, [serviceTypeTemplates]);

  const filteredServiceTypeTemplates = useMemo(() => {
    let filtered = serviceTypeTemplatesWithSearch;

    if (filters.status) {
      filtered = filtered.filter((template) => template.status === filters.status);
    }

    const query = filters.search?.trim().toLowerCase();
    if (!query) return filtered;

    const r = fuzzysort.go(query, filtered, {
      key: '_search',
      threshold: 0.3,
      limit: 100,
    });
    return r.map((x) => x.obj);
  }, [serviceTypeTemplatesWithSearch, filters]);

  const table = useReactTable({
    getRowId: (row) => row.id,
    data: filteredServiceTypeTemplates,
    columns: SERVICE_TYPE_TEMPLATE_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className='space-y-3'>
      <Header filters={filters} setFilters={setFilters} />
      <div className='overflow-hidden rounded-md border'>
        <DataTable table={table} columns={SERVICE_TYPE_TEMPLATE_COLUMNS} className='overflow-hidden' />
      </div>
    </div>
  );
}

interface HeaderProps {
  filters: Filters;
  setFilters: (v: Filters) => void;
}

function Header({ filters, setFilters }: HeaderProps) {
  const onChange = (v: Partial<Filters>) => {
    setFilters({ ...filters, ...v });
  };

  return (
    <div className='flex items-center justify-between gap-4'>
      <div className='flex w-full items-center gap-2'>
        <Popover
          trigger={
            <Button variant='outline' size='icon'>
              <FilterIcon className='size-4' />
            </Button>
          }
          className='w-[400px] space-y-4 p-4'
        >
          <div className='flex w-full items-center'>
            <Label className='w-1/2'>Status</Label>
            <Select
              options={[
                { label: 'Active', value: 'ACTIVE' },
                { label: 'Inactive', value: 'INACTIVE' },
                { label: 'All', value: 'ALL' },
              ]}
              value={filters.status ?? 'ALL'}
              onChange={(v) => {
                if (v === 'ALL') {
                  onChange({ status: null });
                } else {
                  onChange({ status: v });
                }
              }}
              className='w-full'
            />
          </div>
        </Popover>
        <Input
          placeholder='Search service types...'
          value={filters.search}
          onChange={(e) => onChange({ search: e.target.value })}
          className='max-w-sm'
        />
      </div>
      <CreateServiceTypeTemplateDialog />
    </div>
  );
}
